import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Package, CheckCircle2, RotateCcw,
  ArrowRight, Star, Clock, Search,
  Home, Tag, ChevronRight, User, Eye, Ban, LifeBuoy,
  Truck, PackageCheck,
} from "lucide-react";

import type { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToStoreGuestOrders,
  markStoreItemsDelivered,
  StoreOrder,
} from "../../../../redux/storeOrdersSlice";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
} from "../../../../redux/storeProductSlice";

/* ═══════════════════════════════════════════════
   HEARTLAS TOKENS — the same values HomeCentral and
   the store pages use. No local palette, no drift.
═══════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",
  border: "#EEDFC4",
  divider: "#F3E6CE",
  green: "#1F7A45",
  greenSoft: "#E8F4EC",
};

const GRAD_RED = `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`;
/* the Heartlas sweep — dark → gold → red → gold → dark */
const SWEEP = `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.goldDark}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`;

/* support lives at the route HomeCentral already points to */
const supportRoute = (userId?: string) => `/CustomerSupport/${userId ?? ""}`;

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:99px;}
  input,button{font-family:inherit;cursor:pointer;}
  input::placeholder{color:${HEART.textFaint};}
  :focus-visible{outline:2px solid ${HEART.gold};outline-offset:3px;border-radius:8px;}

  @keyframes hcSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes hcRise{from{opacity:0;transform:translateY(14px) scale(.98)}to{opacity:1;transform:none}}
  @keyframes hcBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes hcWiggle{0%,100%{transform:rotate(-3deg)}50%{transform:rotate(3deg)}}

  /* ── 3D slab order card ── */
  .oc{
    background:${HEART.panelBg};border:3px solid ${HEART.dark};border-radius:24px;overflow:hidden;
    box-shadow:0 7px 0 ${HEART.dark},0 16px 30px rgba(27,14,6,.14);
    transition:transform .16s ease,box-shadow .16s ease;
    animation:hcRise .38s ease both;margin-bottom:18px;
  }
  .oc:hover{transform:translateY(-4px);box-shadow:0 11px 0 ${HEART.dark},0 22px 38px rgba(27,14,6,.18);}
  .oc.open{box-shadow:0 7px 0 ${HEART.red},0 18px 34px rgba(200,32,46,.24);}
  .oc-head{display:flex;align-items:stretch;cursor:pointer;background:${HEART.panelBg};}
  .oc-head:active{transform:translateY(2px);}

  /* ── domed item tile ── */
  .it{
    border:2.5px solid ${HEART.dark};border-radius:18px;overflow:hidden;background:${HEART.panelBg};
    box-shadow:0 4px 0 ${HEART.dark};transition:transform .16s ease,box-shadow .16s ease;
    display:flex;flex-direction:column;
  }
  .it:hover{transform:translateY(-3px);box-shadow:0 7px 0 ${HEART.dark},0 12px 20px rgba(27,14,6,.16);}
  .it:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .it-media{position:relative;width:100%;aspect-ratio:1/1;background:${HEART.goldSoft};cursor:pointer;}
  .it-dome-tl{
    position:absolute;top:0;left:0;padding:6px 12px;border-bottom-right-radius:26px;z-index:3;
    background:linear-gradient(135deg,rgba(27,14,6,.9),${HEART.red});
    color:#fff;font-size:10.5px;font-weight:800;display:flex;align-items:center;gap:4px;
    max-width:88%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-dome-br{
    position:absolute;bottom:0;right:0;padding:7px 13px;border-top-left-radius:26px;z-index:3;
    background:linear-gradient(135deg,#4A3220,${HEART.dark} 55%,${HEART.goldDark});
    color:#fff;font-size:12.5px;font-weight:900;white-space:nowrap;
    font-family:'Sora',sans-serif;letter-spacing:-.3px;
  }
  .it-pill{
    position:absolute;bottom:7px;left:7px;padding:4px 10px;border-radius:999px;z-index:3;
    background:linear-gradient(135deg,${HEART.goldSoft},${HEART.gold},${HEART.goldDark});
    color:${HEART.dark};font-size:10px;font-weight:800;
    max-width:70%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  }
  .it-foot{padding:9px 11px 11px;border-top:2.5px solid ${HEART.dark};background:${HEART.bg};}

  /* ── buttons ── */
  .btn{
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:8px 15px;border-radius:999px;font-size:12px;font-weight:800;
    font-family:'Sora',sans-serif;white-space:nowrap;
    border:2.5px solid ${HEART.dark};background:${HEART.panelBg};color:${HEART.dark};
    box-shadow:0 4px 0 ${HEART.dark};transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .btn:hover{transform:translateY(-2px);box-shadow:0 6px 0 ${HEART.dark};}
  .btn:active{transform:translateY(3px);box-shadow:0 1px 0 ${HEART.dark};}
  .btn.gr{background:${GRAD_RED};color:#fff;}
  .btn.gd{background:${HEART.goldSoft};color:${HEART.dark};}
  .btn.gn{background:${HEART.greenSoft};color:${HEART.green};}
  .btn.rd{background:${HEART.redSoft};color:${HEART.redDark};}
  .btn.sm{padding:5px 11px;font-size:10.5px;border-width:2px;box-shadow:0 3px 0 ${HEART.dark};}
  .btn.sm:hover{box-shadow:0 5px 0 ${HEART.dark};}
  .btn.sm:active{box-shadow:0 1px 0 ${HEART.dark};}

  /* ── slim inline filter pills ── */
  .fc{
    display:inline-flex;align-items:center;gap:6px;flex-shrink:0;
    padding:7px 13px;border-radius:999px;border:2px solid ${HEART.dark};
    background:${HEART.panelBg};color:${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    font-family:'Sora',sans-serif;font-size:11.5px;font-weight:800;
    transition:transform .14s ease,box-shadow .14s ease,background .2s;
  }
  .fc:hover{transform:translateY(-2px);box-shadow:0 5px 0 ${HEART.dark};background:${HEART.goldSoft};}
  .fc:active{transform:translateY(2px);box-shadow:0 1px 0 ${HEART.dark};}
  .fc.on{background:${GRAD_RED};color:#fff;border-color:${HEART.dark};}
  .fc-n{
    display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;
    padding:0 5px;border-radius:999px;font-size:10.5px;font-weight:900;
    background:${HEART.goldSoft};color:${HEART.dark};
  }
  .fc.on .fc-n{background:rgba(255,255,255,.92);color:${HEART.redDark};}

  /* ── search ── */
  .sw{
    display:flex;align-items:center;flex:1;background:${HEART.panelBg};border-radius:14px;
    overflow:hidden;border:2px solid ${HEART.dark};box-shadow:0 3px 0 ${HEART.dark};
    transition:box-shadow .16s ease;
  }
  .sw:focus-within{box-shadow:0 3px 0 ${HEART.red};}

  .tab{
    display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:800;
    color:${HEART.textFaint};padding:4px 10px;border:none;background:none;transition:color .18s;
    font-family:'DM Sans',sans-serif;
  }
  .tab.on{color:${HEART.red};}

  @media (prefers-reduced-motion: reduce){
    .oc,.it,.btn,.fc{animation:none!important;transition:none!important;}
  }
  @media (max-width: 520px){ .items-grid{grid-template-columns:1fr 1fr!important;} }
`;

/* ─── helpers ─── */
const fmt = (n?: number) => (!n || isNaN(n) ? "0.00" : Number(n).toFixed(2));
const fmtD = (ts: any) => {
  if (!ts) return "—";
  const ms = ts?.seconds ? ts.seconds * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

type SK = "Pending" | "Processing" | "Delivered" | "Cancelled" | "Refunded";

const SCFG: Record<SK, { bg: string; tx: string; Icon: any }> = {
  Pending:    { bg: HEART.goldSoft,  tx: HEART.goldDark,  Icon: Clock        },
  Processing: { bg: HEART.redSoft,   tx: HEART.redDark,   Icon: Package      },
  Delivered:  { bg: HEART.greenSoft, tx: HEART.green,     Icon: CheckCircle2 },
  Cancelled:  { bg: HEART.divider,   tx: HEART.textMuted, Icon: Ban          },
  Refunded:   { bg: HEART.goldSoft,  tx: HEART.dark,      Icon: RotateCcw    },
};

const statusOf = (o: StoreOrder): SK => {
  if (o.isCancelled) return "Cancelled";
  if (o.isRefunded) return "Refunded";
  if (o.isDelivered) return "Delivered";
  if (o.isPaid) return "Processing";
  return "Pending";
};

const shipmentState = (o: StoreOrder) => {
  const brands: any[] = Array.isArray((o as any).brands) ? (o as any).brands : [];
  const shipped = brands.filter(b => b.deliveryStatus?.sellerDelivered === true);
  const pendingIds = [...new Set(
    shipped.filter(b => b.deliveryStatus?.buyerReceived !== true)
           .map(b => b.brandId).filter(Boolean)
  )] as string[];

  return {
    hasBrands: brands.length > 0,
    total: brands.length,
    shippedCount: shipped.length,
    confirmedCount: brands.filter(b => b.deliveryStatus?.buyerReceived === true).length,
    pendingIds,
  };
};

/* Brand rows are the source of truth for delivery: the seller stamps
   sellerDelivered there, the buyer stamps buyerReceived, and isDelivered
   only flips once every brand has all three flags. Legacy docs with no
   brands[] return hasBrands:false — the thunk rejects those, so the UI
   hides the button rather than firing a call that throws. */
/* items grouped by brand, in first-seen order — one section per seller */
const groupItemsByBrand = (items: any[]) => {
  const map = new Map<string, any[]>();
  const order: string[] = [];
  items.forEach((item) => {
    const bId = item.brandId || "unknown";
    if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
    map.get(bId)!.push(item);
  });
  return order.map((brandId) => ({ brandId, items: map.get(brandId)! }));
};

/* per-brand shipment flags, read straight off order.brands[] */
const brandDeliveryInfo = (o: StoreOrder, brandId: string) => {
  const brands: any[] = Array.isArray((o as any).brands) ? (o as any).brands : [];
  const entry = brands.find((b) => b.brandId === brandId);
  return {
    hasEntry: !!entry,
    sellerDelivered: entry?.deliveryStatus?.sellerDelivered === true,
    buyerReceived: entry?.deliveryStatus?.buyerReceived === true,
    brandName: entry?.brandName || entry?.name || null,
  };
};

const Badge: React.FC<{ s: SK }> = ({ s }) => {
  const c = SCFG[s];
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4, background: c.bg,
      border: `2px solid ${HEART.dark}`, borderRadius: 999, padding: "3px 10px",
      boxShadow: `0 3px 0 ${HEART.dark}`, flexShrink: 0,
    }}>
      <c.Icon size={10} color={c.tx} strokeWidth={2.4} />
      <span style={{ fontSize: 10, fontWeight: 800, color: c.tx, fontFamily: "'Sora',sans-serif" }}>{s}</span>
    </div>
  );
};

/* ── photo with a drawn placeholder, no logo stamp ── */
const Photo: React.FC<{ src?: string | null; alt: string }> = ({ src, alt }) => {
  const [dead, setDead] = useState(false);
  if (src && !dead) {
    return (
      <img src={src} alt={alt} loading="lazy" onError={() => setDead(true)}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    );
  }
  return (
    <div style={{
      width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
      background: `repeating-linear-gradient(45deg,${HEART.goldSoft} 0 10px,${HEART.divider} 10px 20px)`,
    }}>
      <Package size={22} color={HEART.textFaint} strokeWidth={1.8} />
    </div>
  );
};

const FilterPill: React.FC<{
  icon: React.ElementType; label: string; value: number; active: boolean; onClick: () => void;
}> = ({ icon: Icon, label, value, active, onClick }) => (
  <button className={`fc${active ? " on" : ""}`} onClick={onClick}>
    <Icon size={12} color={active ? "#fff" : HEART.textMuted} strokeWidth={2.2} />
    {label}
    <span className="fc-n">{value}</span>
  </button>
);

/* ═══════════════════════════════════════════════
   ITEM TILE — square photo, corner domes, name +
   actions in a bordered footer strip.
═══════════════════════════════════════════════ */
const ItemTile: React.FC<{
  item: any; order: StoreOrder; listingsMap: Record<string, any>; onNav: (p: string) => void; index: number;
}> = ({ item, order, listingsMap, onNav, index }) => {
  const p = listingsMap[item.listingId] ?? {};
  const name = p?.name || item.name || "Item";
  const vMatch: any = item.selectedVariants
    ? Object.values(p?.variants ?? {}).find((v: any) =>
        Object.values(item.selectedVariants).some(
          (sv: any) => String(sv).toLowerCase() === String(v?.name ?? "").toLowerCase()
        )
      )
    : null;
  const unit = item.rate ?? vMatch?.rate ?? p?.rate ?? 0;
  const line = unit * (item.qty ?? 1);
  const variants: string[] = item.selectedVariants
    ? Object.values(item.selectedVariants).map((v: any) => String(v))
    : [];
  const go = () => onNav(`/Store/${item.brandId}`);

  return (
    <div className="it" style={{ animation: "hcRise .4s ease both", animationDelay: `${index * 55}ms` }}>
      <div className="it-media" onClick={go} role="link" tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go()}
        aria-label={`Open the store for ${name}`}>
        <Photo
          src={
            (item.selectedVariants &&
              Object.values(p?.variants ?? {}).find((v: any) =>
                Object.values(item.selectedVariants).some(
                  (sv: any) => String(sv).toLowerCase() === String(v?.name ?? "").toLowerCase()
                )
              ) as any)?.imageUrl ||
            p?.photos?.[0] || item.photo || item.image
          }
          alt={name}
        />

        <div className="it-dome-tl">×{item.qty ?? 1}</div>
        {variants.length > 0 && <span className="it-pill">{variants.slice(0, 2).join(" · ")}</span>}
        <div className="it-dome-br">€{fmt(line)}</div>
      </div>

      <div className="it-foot">
        <div title={name} style={{
          fontSize: 12.5, fontWeight: 700, color: HEART.dark, fontFamily: "'Sora',sans-serif",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 8,
        }}>{name}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn sm gd" onClick={go}><RotateCcw size={10} /> Buy again</button>
          {order.isDelivered && (
            <button className="btn sm gn"
              onClick={() => onNav(`/storeproductreview/${order.guestId}/${item.listingId}/${order.id}`)}>
              <Star size={10} /> Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   ORDER CARD — slab rectangle, taps open to reveal
   the items grid + payment summary.
═══════════════════════════════════════════════ */
const OrderCard: React.FC<{
  order: StoreOrder; listingsMap: Record<string, any>;
  onNav: (p: string) => void; onSupport: () => void;
  onConfirm: (o: StoreOrder, brandIds: string[]) => void;
  delay: number;
}> = ({ order, listingsMap, onNav, onSupport, onConfirm, delay }) => {
  const [open, setOpen] = useState(false);
  const items = order.items || [];
  const fi = items[0];
  const prod = listingsMap[fi?.listingId] ?? {};
  const st = statusOf(order);
  const cnt = items.length;
  const ordId = (order.paymentId || order.id || "").slice(0, 14).toUpperCase();
  const live = !order.isDelivered && !order.isCancelled && !order.isRefunded;
  const ship = shipmentState(order);
  const brandGroups = useMemo(() => groupItemsByBrand(items), [items]);

  return (
    <motion.div layout className={`oc${open ? " open" : ""}`} style={{ animationDelay: `${delay * 55}ms` }}>
      <div style={{ height: 4, background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite" }} />

      <div className="oc-head" onClick={() => setOpen(v => !v)} role="button" tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen(v => !v)}>
        <div style={{ width: 104, height: 104, flexShrink: 0, borderRight: `3px solid ${HEART.dark}`, overflow: "hidden" }}>
          <Photo
            src={
              (Object.values(prod?.variants ?? {}).find((v: any) => v?.imageUrl) as any)?.imageUrl ||
              prod?.photos?.[0] || fi?.photo || fi?.image
            }
            alt={prod?.name || fi?.name || "Order"}
          />
        </div>

        <div style={{ flex: 1, padding: "12px 15px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 800, color: HEART.dark, fontFamily: "'Sora',sans-serif",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -.2,
              }}>
                {prod?.name || fi?.name || "Order"}
                {cnt > 1 && <span style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600, marginLeft: 6 }}>+{cnt - 1} more</span>}
              </div>
              <div style={{ fontSize: 10, color: HEART.textMuted, marginTop: 4, fontWeight: 600 }}>
                #{ordId} · {fmtD(order.createdAt)} · {cnt} item{cnt > 1 ? "s" : ""}
              </div>
            </div>
            <Badge s={st} />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: HEART.red, fontFamily: "'Sora',sans-serif", letterSpacing: -.5 }}>
              €{fmt(order.totalPaid)}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800,
              color: HEART.textMuted, letterSpacing: ".05em", textTransform: "uppercase",
            }}>
              {open ? "Hide items" : "See items"}
              <ChevronRight size={15} color={HEART.red}
                style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .22s" }} />
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .28, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 18, borderTop: `3px solid ${HEART.dark}`, background: HEART.bg }}>
              <p style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".2em", color: HEART.textMuted, marginBottom: 12 }}>
                WHAT'S IN THIS ORDER
              </p>

              {brandGroups.map((group, gi) => {
                const info = brandDeliveryInfo(order, group.brandId);
                const firstItem = group.items[0];
                const brandName = info.brandName || firstItem?.brandName || firstItem?.vendor || "Seller";
                const pending = live && info.sellerDelivered && !info.buyerReceived;
                const notShippedYet = live && info.hasEntry && !info.sellerDelivered;
                const alreadyConfirmed = info.buyerReceived;

                return (
                  <div key={group.brandId} style={{ marginBottom: gi < brandGroups.length - 1 ? 22 : 18 }}>
                    {/* ── brand header bar: name left, per-brand confirm button far right ── */}
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                      background: HEART.panelBg, border: `2.5px solid ${HEART.dark}`, borderRadius: 14,
                      padding: "10px 14px", boxShadow: `0 4px 0 ${HEART.dark}`, marginBottom: 12,
                    }}>
                      <span style={{
                        fontSize: 13, fontWeight: 800, color: HEART.dark, fontFamily: "'Sora',sans-serif",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                      }}>
                        {brandName}
                      </span>

                      {pending ? (
                        <button className="btn sm gr" onClick={() => onConfirm(order, [group.brandId])} style={{ flexShrink: 0 }}>
                          <PackageCheck size={11} /> Confirm received
                        </button>
                      ) : notShippedYet ? (
                        <span className="btn sm gd" style={{ cursor: "default", flexShrink: 0 }}>
                          <Truck size={11} /> Not shipped yet
                        </span>
                      ) : alreadyConfirmed ? (
                        <span className="btn sm gn" style={{ cursor: "default", flexShrink: 0 }}>
                          <CheckCircle2 size={11} /> Received
                        </span>
                      ) : null}
                    </div>

                    {/* ── that brand's items ── */}
                    <div className="items-grid" style={{
                      display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 14,
                    }}>
                      {group.items.map((item: any, i: number) => (
                        <ItemTile key={(item.listingId || item.id) + i} item={item} order={order}
                          listingsMap={listingsMap} onNav={onNav} index={i} />
                      ))}
                    </div>

                    {/* ── 3D sweep bar between brand sections ── */}
                    {gi < brandGroups.length - 1 && (
                      <div style={{
                        height: 7, borderRadius: 999, marginTop: 20,
                        background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite",
                        border: `2px solid ${HEART.dark}`, boxShadow: `0 3px 0 ${HEART.dark}`,
                      }} />
                    )}
                  </div>
                );
              })}

              <div style={{
                marginTop: 6, background: HEART.panelBg, border: `2.5px solid ${HEART.dark}`, borderRadius: 18,
                padding: "14px 16px", boxShadow: `0 5px 0 ${HEART.dark}`,
              }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".18em", color: HEART.textMuted, marginBottom: 10 }}>
                  PAYMENT SUMMARY
                </div>
                {[
                  { l: "Subtotal",    v: `€${fmt(order.subtotal)}` },
                  { l: "Service fee", v: `€${fmt(order.guestsFee)}` },
                  { l: "Delivery",    v: order.deliveryFee ? `€${fmt(order.deliveryFee)}` : "Free" },
                ].map(({ l, v }, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${HEART.divider}` }}>
                    <span style={{ fontSize: 12, color: HEART.textMuted }}>{l}</span>
                    <span style={{ fontSize: 12, color: HEART.dark, fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: HEART.dark, fontFamily: "'Sora',sans-serif" }}>Total paid</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: HEART.red, fontFamily: "'Sora',sans-serif", letterSpacing: -.4 }}>
                    €{fmt(order.totalPaid)}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
                {ship.total > 1 && (
                  <span style={{ fontSize: 10.5, color: HEART.textMuted, fontWeight: 600 }}>
                    {ship.confirmedCount} of {ship.total} parcels confirmed
                  </span>
                )}

                {live && (
                  <>
                    <button className="btn rd" onClick={onSupport}>
                      <Ban size={12} /> Cancel order
                    </button>
                    <span style={{ fontSize: 10.5, color: HEART.textMuted, fontWeight: 600 }}>
                      Support handles cancellations.
                    </span>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
const StoreBuyerOrders: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const guestId = userId ?? authUser?.userId ?? "";

  const orders = useSelector((s: RootState) => (s as any).storeOrders?.guestOrders ?? []);
  const listings = useSelector((s: RootState) => (s as any).storeProducts?.filteredProducts ?? []);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | SK>("all");
  const [isMob, setIsMob] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!guestId) return;
    const unsub = dispatch(listenToStoreGuestOrders(guestId) as any);
    dispatch(listenToPublishedProducts() as any);
    dispatch(filterLocalProductsByCountry(null) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [guestId, dispatch]);

  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    (listings as any[]).forEach((l: any) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  const sorted = useMemo(
    () => [...(orders as StoreOrder[])].sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)),
    [orders]
  );

  const cnt = (s: SK) => sorted.filter(o => statusOf(o) === s).length;
  const pendingN = cnt("Pending");
  const processingN = cnt("Processing");
  const deliveredN = cnt("Delivered");
  const cancelledN = cnt("Cancelled");
  const activeN = pendingN + processingN;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(o => {
      const status = statusOf(o);
      if (filter === "all") {
        if (status === "Delivered" || status === "Cancelled" || status === "Refunded") return false;
      } else if (status !== filter) {
        return false;
      }
      if (!q) return true;
      const fi = o.items?.[0]; const p = listingsMap[fi?.listingId] ?? {};
      return `${p?.name ?? ""} ${o.id ?? ""} ${fi?.name ?? ""}`.toLowerCase().includes(q);
    });
  }, [sorted, filter, search, listingsMap]);

  /* cancelling is a support conversation for now — the thunk stays unwired */
  const goToSupport = () => navigate(supportRoute(guestId));

  /* buyer half of the same thunk the seller uses; role:"buyer" stamps
     only buyerReceived, so an unshipped brand can't be closed out early */
  const onConfirm = (o: StoreOrder, brandIds: string[]) => {
    if (!brandIds.length) return;
    dispatch(markStoreItemsDelivered({ orderId: o.id, brandIds, role: "buyer" }) as any);
  };

  /* ── nothing ordered yet ── */
  if (sorted.length === 0) return (
    <div style={{ minHeight: "100vh", background: HEART.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{
        textAlign: "center", maxWidth: 380, margin: "0 20px", background: HEART.panelBg,
        border: `3px solid ${HEART.dark}`, borderRadius: 28, padding: "40px 30px",
        boxShadow: `0 8px 0 ${HEART.dark},0 22px 44px rgba(27,14,6,.16)`,
      }}>
        <div style={{ fontSize: 58, marginBottom: 16, animation: "hcBob 3.2s ease-in-out infinite" }}>🛍️</div>
        <h2 style={{ fontWeight: 900, fontSize: 26, color: HEART.dark, marginBottom: 8, fontFamily: "'Sora',sans-serif", letterSpacing: -.5 }}>
          Nothing here yet
        </h2>
        <p style={{ color: HEART.textMuted, fontSize: 13.5, lineHeight: 1.7, marginBottom: 24 }}>
          Everything you buy shows up on this page, with tracking and receipts.
        </p>
        <button className="btn gr" style={{ fontSize: 13.5, padding: "13px 28px" }} onClick={() => navigate(`/StoreProductsHome/${guestId}`)}>
          <ShoppingBag size={14} /> Start shopping <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: HEART.bg, fontFamily: "'DM Sans',sans-serif", paddingBottom: isMob ? 90 : 40 }}>
      <style>{CSS}</style>

      {/* ══ HEADER — title and search share one row, pills sit under ══ */}
      <div style={{
        background: "rgba(251,246,238,.94)", backdropFilter: "blur(14px)",
        borderBottom: `2px solid ${HEART.dark}`, position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ height: 3, background: SWEEP, backgroundSize: "300% 100%", animation: "hcSpin 4s linear infinite" }} />
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "10px 20px 9px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontWeight: 900, fontSize: isMob ? 19 : 22, color: HEART.dark, letterSpacing: -.5, lineHeight: 1.1, fontFamily: "'Sora',sans-serif" }}>
              My orders
            </h1>
            {activeN > 0 && (
              <p style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600, marginTop: 1 }}>{activeN} on the way</p>
            )}
          </div>

          <div className="sw" style={{ marginLeft: "auto", maxWidth: 340 }}>
            <div style={{ padding: "0 11px" }}><Search size={13} color={HEART.textFaint} /></div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders"
              style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 12.5, fontWeight: 600, color: HEART.dark, padding: "8px 11px 8px 0" }} />
          </div>
        </div>

        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", gap: 8, overflowX: "auto", padding: "0 20px 10px", scrollbarWidth: "none" as any }}>
          <FilterPill icon={ShoppingBag}  label="Active"    value={activeN}     active={filter === "all"}        onClick={() => setFilter("all")} />
          <FilterPill icon={Clock}        label="Pending"   value={pendingN}    active={filter === "Pending"}    onClick={() => setFilter("Pending")} />
          <FilterPill icon={Package}      label="Packing"   value={processingN} active={filter === "Processing"} onClick={() => setFilter("Processing")} />
          <FilterPill icon={CheckCircle2} label="Delivered" value={deliveredN}  active={filter === "Delivered"}  onClick={() => setFilter("Delivered")} />
          <FilterPill icon={Ban}          label="Cancelled" value={cancelledN}  active={filter === "Cancelled"}  onClick={() => setFilter("Cancelled")} />
        </div>
      </div>

      {/* ══ ORDER LIST ══ */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: isMob ? "20px 14px" : "26px 20px" }}>
        {filtered.length === 0 ? (
          <div style={{
            textAlign: "center", background: HEART.panelBg, border: `3px solid ${HEART.dark}`, borderRadius: 26,
            padding: "44px 28px", boxShadow: `0 7px 0 ${HEART.dark}`,
          }}>
            <div style={{ fontSize: 46, marginBottom: 14, animation: "hcWiggle 2.6s ease-in-out infinite" }}>🔍</div>
            <h3 style={{ fontWeight: 900, fontSize: 19, color: HEART.dark, marginBottom: 8, fontFamily: "'Sora',sans-serif" }}>No orders match</h3>
            <p style={{ color: HEART.textMuted, fontSize: 13, marginBottom: 22, fontWeight: 600 }}>
              Clear the search or pick another filter above.
            </p>
            <button className="btn gr" onClick={() => { setSearch(""); setFilter("all"); }}>
              <RotateCcw size={12} /> Reset filters
            </button>
          </div>
        ) : (
          <>
            {filtered.map((order, i) => (
              <OrderCard key={order.id} order={order} listingsMap={listingsMap}
                onNav={path => navigate(path)} onSupport={goToSupport}
                onConfirm={onConfirm} delay={i} />
            ))}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
              <button className="btn gd" onClick={goToSupport}>
                <LifeBuoy size={13} /> Something wrong with an order?
              </button>
            </div>
          </>
        )}
      </div>

     
    </div>
  );
};

export default StoreBuyerOrders;